import type { MetricTableV1 } from "@graftus/ui-parts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { MemberMention } from "../MemberMention";
import type { IntelligenceMemberRoster } from "../intelligenceMarkdownUtils";

function formatPeriod(period: MetricTableV1["period"], locale: string): string | null {
  if (!period) return null;
  const fmt = (day: string) => {
    const d = new Date(`${day}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? day
      : d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  };
  return `${fmt(period.from)} – ${fmt(period.to)}`;
}

/**
 * A `data-metric-table` part: a small table of numbers per row. Member
 * cells render as the same pill the prose uses, numbers right-aligned.
 */
export function MetricTablePart({
  table,
  businessId,
  roster,
  locale,
}: {
  table: MetricTableV1;
  businessId?: string;
  roster?: IntelligenceMemberRoster | null;
  locale: string;
}) {
  const period = formatPeriod(table.period, locale);
  return (
    <figure className="w-fit max-w-full overflow-hidden rounded-md border border-border bg-background text-sm">
      {(table.title || period || table.source) && (
        <figcaption className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
          {table.source?.domain ? (
            <img
              src={`https://www.google.com/s2/favicons?domain=${table.source.domain}&sz=32`}
              alt=""
              className="h-3.5 w-3.5 shrink-0 rounded-[3px]"
            />
          ) : null}
          {table.title ? <span className="font-medium text-foreground/90">{table.title}</span> : null}
          {period ? <span>{period}</span> : null}
        </figcaption>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {table.columns.map((column) => (
              <TableHead
                key={column.key}
                className={cn("h-8 px-3 text-xs", column.kind === "number" && "text-right")}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.rows.map((row, index) => (
            <TableRow key={row.memberId ?? index}>
              {table.columns.map((column) => {
                const value = row.cells[column.key];
                const text = value == null ? "–" : String(value);
                const isMember = column.kind === "member" && row.memberId && businessId;
                return (
                  <TableCell
                    key={column.key}
                    className={cn(
                      "px-3 py-1.5 text-xs",
                      column.kind === "number" && "text-right tabular-nums",
                    )}
                  >
                    {isMember ? (
                      <MemberMention
                        businessId={businessId}
                        memberId={row.memberId!}
                        displayName={text}
                        roster={roster ?? null}
                      />
                    ) : (
                      text
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {table.note ? (
        <div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
          {table.note}
        </div>
      ) : null}
    </figure>
  );
}
